import { useState, useEffect } from 'react';
import useSupabase from './useSupabase';

const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [rootCategories, setRootCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { fetchCategories } = useSupabase();

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get all categories from database
        const categoryData = await fetchCategories();
        
        // Extract all category names for simple dropdown usage
        const allCategoryNames = [...new Set(categoryData.map(cat => cat.name))].sort();
        
        // Extract root categories (parent_id is null) for search suggestions
        const rootCategoryNames = [...new Set(
          categoryData
            .filter(cat => !cat.parent_id) // Only root categories
            .map(cat => cat.name)
        )].sort();
        
        setCategories(allCategoryNames);
        setRootCategories(rootCategoryNames);
        
        console.log('✅ Categories loaded:', {
          all: allCategoryNames.length,
          root: rootCategoryNames.length,
          rootList: rootCategoryNames
        });
      } catch (err) {
        console.error('❌ Error loading categories:', err);
        setError(err.message);
        
        // Fallback to hardcoded categories if database fails
        const fallbackCategories = [
          'Furniture',
          'Decor', 
          'Lighting',
          'Textiles',
          'Costumes',
          'Electronics',
          'Musical Instruments',
          'Tools & Hardware',
          'Clothing & Accessories',
          'Other'
        ];
        setCategories(fallbackCategories);
        setRootCategories(fallbackCategories); // All are root in fallback
        console.log('⚠️ Using fallback categories due to error');
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, [fetchCategories]);

  // Refresh categories (for when new ones are added)
  const refreshCategories = async () => {
    try {
      const categoryData = await fetchCategories();
      const allCategoryNames = [...new Set(categoryData.map(cat => cat.name))].sort();
      const rootCategoryNames = [...new Set(
        categoryData
          .filter(cat => !cat.parent_id)
          .map(cat => cat.name)
      )].sort();
      
      setCategories(allCategoryNames);
      setRootCategories(rootCategoryNames);
      return true;
    } catch (err) {
      console.error('Error refreshing categories:', err);
      return false;
    }
  };

  return {
    categories,        // All categories for dropdowns
    rootCategories,    // Only root categories for search suggestions
    loading,
    error,
    refreshCategories
  };
};

export default useCategories;
